/** Cascade layer that holds the rules styling the button root itself. */
export const BUTTON_ROOT_LAYER = `mp-button-root`;

const CONDITIONAL_AT_RULE = /^@(media|supports|container)\b/;

/**
 * Moves the rules whose subject is the button root into the `BUTTON_ROOT_LAYER` cascade layer and leaves
 * every other rule unlayered:
 *
 *   `.mp-button-root[data-button-size="small"] {…}`                      -> layered
 *   `.mp-button-root[data-button-size="small"] .mp-button-container {…}` -> unlayered
 *
 * Unlayered rules beat layered ones whatever their specificity, so a caller's class on the button
 * overrides its size and theme rules, as outer-page rules overrode the Panel Button's `:host` rules.
 * Inside the layer, the root rules still rank against each other by specificity. The rules for the
 * button's insides stay unlayered, so the page's less specific rules can't restyle them.
 *
 * `rootSelector` is the simple selector that marks the root, e.g. `.mp-button-root` or an instance
 * attribute. A selector list that mixes root and non-root selectors is split between the two.
 * `layerName` names the layer, for a component other than the button.
 */
export function layerRootRules(css: string, rootSelector: string, layerName: string = BUTTON_ROOT_LAYER): string {
  const {rootRules, otherRules} = partitionRules(css, rootSelector);
  return rootRules ? `@layer ${layerName}{${rootRules}}${otherRules}` : otherRules;
}

function partitionRules(css: string, rootSelector: string): {rootRules: string; otherRules: string} {
  let rootRules = ``;
  let otherRules = ``;
  for (const block of parseBlocks(css)) {
    if (block.body === null) {
      otherRules += block.prelude;
    } else if (block.prelude.startsWith(`@`)) {
      if (CONDITIONAL_AT_RULE.test(block.prelude)) {
        const nested = partitionRules(block.body, rootSelector);
        rootRules += nested.rootRules ? `${block.prelude}{${nested.rootRules}}` : ``;
        otherRules += nested.otherRules ? `${block.prelude}{${nested.otherRules}}` : ``;
      } else {
        otherRules += `${block.prelude}{${block.body}}`;
      }
    } else {
      const selectors = splitTopLevel(block.prelude, `,`).map((selector) => selector.trim());
      const rootSelectors = selectors.filter((selector) => targetsRoot(selector, rootSelector));
      const otherSelectors = selectors.filter((selector) => !targetsRoot(selector, rootSelector));
      rootRules += rootSelectors.length ? `${rootSelectors.join(`,`)}{${block.body}}` : ``;
      otherRules += otherSelectors.length ? `${otherSelectors.join(`,`)}{${block.body}}` : ``;
    }
  }
  return {rootRules, otherRules};
}

/**
 * True when the selector's subject, its last compound selector, carries `rootSelector` outside any
 * pseudo-class argument. `:host(.mp-button-root)` and `a:not(.mp-button-root)` are not root rules.
 */
function targetsRoot(selector: string, rootSelector: string): boolean {
  const compounds = splitTopLevel(selector, /[\s>+~]/);
  const subject = withoutArguments(compounds[compounds.length - 1] ?? ``);
  let index = subject.indexOf(rootSelector);
  while (index !== -1) {
    const next = subject[index + rootSelector.length];
    if (next === undefined || !/[\w-]/.test(next)) {
      return true;
    }
    index = subject.indexOf(rootSelector, index + 1);
  }
  return false;
}

/** Drops every parenthesized argument, so `a:not(.x).y` reads as `a:not.y`. */
function withoutArguments(compound: string): string {
  let depth = 0;
  let result = ``;
  for (const character of compound) {
    if (character === `(`) {
      depth++;
    } else if (character === `)`) {
      depth--;
    } else if (depth === 0) {
      result += character;
    }
  }
  return result;
}

/**
 * Splits on separators that sit outside parentheses, attribute brackets and strings. Empty pieces
 * (from runs of whitespace around a combinator) are dropped.
 */
function splitTopLevel(text: string, separator: string | RegExp): string[] {
  const pieces: string[] = [];
  let depth = 0;
  let quote: string | null = null;
  let start = 0;
  for (let index = 0; index < text.length; index++) {
    const character = text[index];
    if (quote) {
      if (character === `\\`) {
        index++;
      } else if (character === quote) {
        quote = null;
      }
    } else if (character === `"` || character === `'`) {
      quote = character;
    } else if (character === `(` || character === `[`) {
      depth++;
    } else if (character === `)` || character === `]`) {
      depth--;
    } else if (depth === 0 && (typeof separator === `string` ? character === separator : separator.test(character))) {
      pieces.push(text.slice(start, index));
      start = index + 1;
    }
  }
  pieces.push(text.slice(start));
  return pieces.filter((piece) => piece.trim());
}

type Block = {prelude: string; body: string | null};

/**
 * Splits a stylesheet into its top-level blocks. A statement without a body, such as `@import …;`,
 * comes back with a null body and its text, semicolon included, as the prelude. Comments are dropped.
 */
function parseBlocks(css: string): Block[] {
  const blocks: Block[] = [];
  let prelude = ``;
  let index = 0;
  while (index < css.length) {
    const character = css[index];
    if (character === `/` && css[index + 1] === `*`) {
      const end = css.indexOf(`*/`, index + 2);
      index = end === -1 ? css.length : end + 2;
    } else if (character === `"` || character === `'`) {
      const end = findStringEnd(css, index);
      prelude += css.slice(index, end);
      index = end;
    } else if (character === `;`) {
      if (prelude.trim()) {
        blocks.push({prelude: `${prelude.trim()};`, body: null});
      }
      prelude = ``;
      index++;
    } else if (character === `{`) {
      const end = findBlockEnd(css, index);
      blocks.push({prelude: prelude.trim(), body: css.slice(index + 1, end)});
      prelude = ``;
      index = end + 1;
    } else {
      prelude += character;
      index++;
    }
  }
  return blocks;
}

/** Index of the `}` that closes the `{` at `openIndex`. */
function findBlockEnd(css: string, openIndex: number): number {
  let depth = 0;
  let index = openIndex;
  while (index < css.length) {
    const character = css[index];
    if (character === `/` && css[index + 1] === `*`) {
      const end = css.indexOf(`*/`, index + 2);
      index = end === -1 ? css.length : end + 2;
      continue;
    }
    if (character === `"` || character === `'`) {
      index = findStringEnd(css, index);
      continue;
    }
    if (character === `{`) {
      depth++;
    } else if (character === `}`) {
      depth--;
      if (depth === 0) {
        return index;
      }
    }
    index++;
  }
  return css.length;
}

/** Index just past the string literal that opens at `openIndex`. */
function findStringEnd(css: string, openIndex: number): number {
  const quote = css[openIndex];
  let index = openIndex + 1;
  while (index < css.length && css[index] !== quote) {
    index += css[index] === `\\` ? 2 : 1;
  }
  return index + 1;
}
